import { useContext, useLayoutEffect, useMemo, useRef, useState } from "react";

import { observer } from "mobx-react-lite";

import { InlineNode } from "@/components/expression/InlineNode";
import { drawPath, getIntermediatePoint } from "@/components/expression/svgUtils/drawPath";
import { useNodePoints } from "@/components/expression/svgUtils/useNodePoints";
import { calculateGroupDepthLevel } from "@/components/expression/utils/groups";
import { SelectedContext } from "@/components/expression/utils/layout";
import { NodeStyleProvider, useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { useNodeHover } from "@/components/expression/utils/useNodeHover";
import { isLabelChild } from "@/models/nodes/utils";
import { twMerge } from "@/utils/twMerge";

import type { ExpressionComponent } from "./types";
import type { Point } from "@/components/expression/svgUtils/types";

const SQUARE_ROOT_BORDER_RADIUS = 5; // in px
const SQUARE_ROOT_BORDER_RADIUS_STEP = 4; // in px

export const SquareRoot: ExpressionComponent<"operator"> = observer(function SquareRoot(props) {
  const { node, className } = props;
  const { parameters, doc } = node;
  const [rootIndex, rootBody] = parameters;

  const { isHovered, handlerProps } = useNodeHover(node);
  const { view, text } = useNodeStyle();
  const isContextSelected = useContext(SelectedContext);

  const isActive = doc.selection.isActive(node);
  const isFocused = doc.cursor.nodesAreFocused(rootBody ? [rootBody] : []);

  const isSelected = isContextSelected || isActive || doc.selection.isSelected(node);

  const isTextView = view === "text";
  const isParameterChild = text === "parameter";

  const groupDepthLevel = useMemo(
    () => (rootBody ? calculateGroupDepthLevel(rootBody) : 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rootBody, rootBody?.descendants.length],
  );
  const defaultBorderRadius = isTextView
    ? SQUARE_ROOT_BORDER_RADIUS / 2
    : SQUARE_ROOT_BORDER_RADIUS;

  let progressiveBorderRadius =
    SQUARE_ROOT_BORDER_RADIUS + SQUARE_ROOT_BORDER_RADIUS_STEP * groupDepthLevel;
  progressiveBorderRadius = isTextView ? progressiveBorderRadius / 2 : progressiveBorderRadius;

  const nodeParameterRef = useRef<HTMLDivElement>(null);
  const { viewBox: parameterViewBox } = useNodePoints(nodeParameterRef, rootIndex);

  const nodeWrapperRef = useRef<HTMLDivElement>(null);
  const { nodePoints, viewBox } = useNodePoints(nodeWrapperRef, node);
  const [nodeCustomPoints, setNodeCustomPoints] = useState<{
    customPoint1: Point;
    customPoint2: Point;
    customPoint3: Point;
  }>({
    customPoint1: { x: 0, y: 0 },
    customPoint2: { x: 0, y: 0 },
    customPoint3: { x: 0, y: 0 },
  });

  useLayoutEffect(() => {
    const angleInDegrees = isTextView ? 3 : 5; // 5°
    const tanOfAngle = Math.tan((angleInDegrees * Math.PI) / 180); // calculate tangent of angle in degrees
    const adjacentLine = viewBox.height - SQUARE_ROOT_BORDER_RADIUS;

    const customPoint1 = {
      x: -(tanOfAngle * adjacentLine) + 1,
      y: viewBox.height - 1,
    };
    const customPoint2 = {
      x: customPoint1.x - (isTextView ? 2 : 5),
      y: viewBox.height / 2,
    };
    const customPoint3 = {
      x: customPoint1.x - (isTextView ? 3 : 7),
      y: viewBox.height / 2,
    };

    setNodeCustomPoints({
      customPoint1,
      customPoint2,
      customPoint3,
    });
  }, [viewBox, isTextView]);

  const shiftXValue = useMemo(() => {
    return Math.abs(nodeCustomPoints.customPoint3.x) + 2;
  }, [nodeCustomPoints.customPoint3.x]);

  const squareRootPath = useMemo(() => {
    const textViewShift = isTextView ? 1 : 0;
    const startPoint = {
      x: nodePoints.tr.x - (isTextView ? 3 : 6),
      y: nodePoints.tr.y + textViewShift,
    };

    const tailPoint = getIntermediatePoint(
      {
        ...startPoint,
        x: startPoint.x + 2,
      },
      {
        x: nodePoints.rc.x + 4,
        y: nodePoints.rc.y,
      },
      3,
    );

    const topLeftPoint = {
      ...nodePoints.tl,
      y: nodePoints.tl.y + textViewShift,
    };

    return drawPath(shiftXValue)
      .moveTo(startPoint)
      .arcTo(startPoint, isTextView ? startPoint : tailPoint, {
        ...startPoint,
        x: startPoint.x + 2.5,
      })
      .moveTo(startPoint)
      .lineTo(topLeftPoint)
      .arcTo(topLeftPoint, nodePoints.lt, {
        ...nodePoints.tla,
        y: nodePoints.tla.y + textViewShift,
      })
      .lineTo(nodeCustomPoints.customPoint1)
      .lineTo(nodeCustomPoints.customPoint2)
      .lineTo(nodeCustomPoints.customPoint3)
      .finish();
  }, [isTextView, nodePoints, nodeCustomPoints, shiftXValue]);

  const backgroundPath = useMemo(() => {
    const startPoint = {
      ...nodePoints.tr,
      x: nodePoints.tr.x - 4,
    };

    return drawPath(shiftXValue)
      .moveTo(startPoint)
      .lineTo(nodePoints.tl)
      .arcTo(nodePoints.tl, nodePoints.lt, nodePoints.tla)
      .lineTo({
        ...nodeCustomPoints.customPoint1,
        y: nodeCustomPoints.customPoint1.y + 1,
      })
      .lineTo({
        x: nodePoints.br.x - 4,
        y: nodePoints.br.y + 1,
      })
      .quadraticCurveTo(
        {
          ...nodePoints.rc,
          x: nodePoints.rc.x + 4,
        },
        startPoint,
      )
      .finish();
  }, [nodePoints, nodeCustomPoints, shiftXValue]);

  return (
    <div
      className={twMerge(
        "relative flex min-w-5 items-baseline justify-center",
        isTextView && "min-w-0",
        isSelected && isTextView && "bg-selected-bg text-selected-fg",
        className,
      )}
    >
      <div
        ref={nodeParameterRef}
        style={{
          left: 2,
          bottom: nodeCustomPoints.customPoint2.y + (isLabelChild(node) ? 1 : 2),
        }}
        className={twMerge(
          "absolute z-30 min-h-2 flex flex-nowrap items-center text-node-xxs text-function-parameter",
          isLabelChild(node) && "pr-1",
        )}
      >
        <NodeStyleProvider
          view="text"
          text="parameter"
          size={isLabelChild(node) ? "xxxs" : "xxs"}
          placeholderVisible={isFocused && !isTextView ? "if-empty" : "none"}
        >
          {rootIndex && <InlineNode node={rootIndex} />}
        </NodeStyleProvider>
      </div>
      <div className="w-0.5 selection:bg-transparent">&nbsp;</div>
      <div style={{ width: parameterViewBox.width - 8 > 0 ? parameterViewBox.width - 8 : 0 }} />
      <div
        {...handlerProps}
        className="relative inline-flex flex-nowrap justify-center items-baseline"
      >
        <div style={{ width: shiftXValue }} className="selection:bg-transparent">
          &nbsp;
        </div>
        <svg
          className={twMerge("absolute top-0 left-0", "w-full h-full", "pointer-events-none")}
          fill="none"
          preserveAspectRatio="none"
        >
          <path
            className={twMerge(
              "fill-transparent",
              isTextView && "fill-transparent",
              isSelected && "fill-selected-bg",
            )}
            d={backgroundPath}
          />
          <path
            className={twMerge(
              "stroke-document-text",
              isParameterChild && "stroke-function-parameter",
              (isHovered || isFocused || isActive) && !isTextView && "stroke-node-active",
            )}
            d={squareRootPath}
            strokeWidth="1.24"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <div
          ref={nodeWrapperRef}
          style={{
            borderTopLeftRadius: defaultBorderRadius,
            borderBottomLeftRadius: progressiveBorderRadius,
          }}
          className="relative"
        >
          <div
            className={twMerge(
              "flex flex-nowrap items-center min-w-6 pt-1 pb-0.75 pl-1 pr-1.5 text-node-sm",
              isTextView && "min-w-2.5 px-1 pb-0 pt-0.5 text-node-xxs",
            )}
          >
            <div className="-ml-2 pointer-events-none selection:bg-transparent">&nbsp;</div>
            {rootBody && (
              <SelectedContext.Provider value={isSelected}>
                <InlineNode node={rootBody} />
              </SelectedContext.Provider>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
